import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/components/utils/utils";
import {
  Plane,
  Calendar,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Briefcase,
  PieChart,
  Gauge,
  Link2,
  ExternalLink,
  Download,
  Building2,
  Palette,
  Lightbulb,
  Settings,
  Share2,
  WifiOff,
  BarChart2,
  LayoutDashboard,
  Library,
  Inbox,
} from "lucide-react";
import { useSidebar } from "@/contexts/SidebarContext";
import type { UserRole } from "@/lib/auth/models/auth.model";
import { useState, useEffect } from "react";
import { useI18n } from "@/hooks/useI18n";

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  userRole: UserRole | null;
  agencyLogoUrl?: string | null;
  agencyLogoSmallUrl?: string | null;
  logoLoading?: boolean;
}

const Sidebar = ({ isCollapsed, onToggle, userRole, agencyLogoUrl, agencyLogoSmallUrl, logoLoading = false }: SidebarProps) => {
  const location = useLocation();
  const { setMobileSidebarOpen, openGroups, setGroupOpen } = useSidebar();
  const { t } = useI18n();
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    setIsStandalone(window.matchMedia('(display-mode: standalone)').matches);
    const handler = (e: Event) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') setInstallPrompt(null);
    } else {
      alert('Per installare l\'app:\n• Chrome/Edge: clicca l\'icona ⊕ nella barra degli indirizzi\n• Safari iOS: tocca Condividi → "Aggiungi a schermata Home"');
    }
  };

  type MenuItem = {
    name: string;
    path: string;
    icon: any;
    isActive: boolean;
    badge?: string | number;
  };

  const isStaff =
    userRole === "agency" ||
    userRole === "admin" ||
    userRole === "travel_advisor_member" ||
    userRole === "travel_designer";
  const showDownloaded = isStandalone || window.location.hostname === 'localhost';

  // I gruppi con titolo diventano accordion; quelli senza (impostazioni in fondo)
  // sono voci semplici. I gruppi vuoti per il ruolo corrente spariscono.
  const menuGroups: Array<{ key: string; title?: string; icon?: any; items: MenuItem[] }> = [
    {
      key: "workspace",
      title: t("sidebar.groups.workspace"),
      icon: Briefcase,
      items: [
        {
          name: t("sidebar.trips"),
          path: "/dashboard",
          icon: Plane,
          isActive: location.pathname === "/dashboard",
        },
        ...(isStaff
          ? [
              {
                name: t("sidebar.requests"),
                path: "/requests",
                icon: Inbox,
                isActive: location.pathname === "/requests",
              },
              {
                name: t("sidebar.templates"),
                path: "/playground",
                icon: Lightbulb,
                isActive: location.pathname === "/playground",
              },
              {
                name: t("sidebar.library"),
                path: "/library",
                icon: Library,
                isActive: location.pathname === "/library",
              },
            ]
          : []),
      ],
    },
    {
      key: "overview",
      title: t("sidebar.overview"),
      icon: LayoutDashboard,
      items: [
        ...(isStaff
          ? [
              {
                name: t("sidebar.overview"),
                path: "/overview",
                icon: Gauge,
                isActive: location.pathname === "/overview",
              },
              {
                name: t("sidebar.overviewData"),
                path: "/overview/data",
                icon: PieChart,
                isActive: location.pathname === "/overview/data",
              },
            ]
          : []),
        // Visibile a tutti i ruoli: il viaggiatore ne vede la versione ridotta (solo le
        // proprie rate, con "Paga" al posto di "Segna come pagata"). Il filtro non e' qui:
        // la RPC restituisce gia' soltanto cio' che l'utente puo' vedere.
        {
          name: t("sidebar.payments"),
          path: "/payments",
          icon: CreditCard,
          isActive: location.pathname === "/payments",
        },
        ...(userRole === "admin"
          ? [
              {
                name: t("sidebar.adminDashboard"),
                path: "/admin",
                icon: BarChart2,
                isActive: location.pathname === "/admin",
              },
            ]
          : []),
      ],
    },
    {
      key: "sharing",
      title: t("sidebar.groups.sharing"),
      icon: Share2,
      items: [
        ...(isStaff
          ? [
              {
                name: t("sidebar.shared"),
                path: "/shared",
                icon: Link2,
                isActive: location.pathname === "/shared",
              },
            ]
          : []),
        ...(showDownloaded
          ? [
              {
                name: t("sidebar.downloaded"),
                path: "/downloaded",
                icon: WifiOff,
                isActive: location.pathname.startsWith("/downloaded"),
              },
            ]
          : []),
      ],
    },
    {
      key: "settings",
      items: [
        {
          name: t("sidebar.settings"),
          path: "/profile?tab=account-settings",
          icon: Settings,
          isActive: location.pathname === "/profile",
        },
      ],
    },
  ].filter((group) => group.items.length > 0);

  const handleMobileClose = () => {
    setMobileSidebarOpen(false);
  };

  // Arrivando su una pagina (link diretto, redirect) si apre la sezione che la contiene,
  // senza richiudere quelle che l'utente ha aperto a mano.
  const activeGroupKey = menuGroups.find(
    (group) => group.title && group.items.some((item) => item.isActive)
  )?.key;
  useEffect(() => {
    if (activeGroupKey) setGroupOpen(activeGroupKey, true);
  }, [activeGroupKey, setGroupOpen]);

  const renderMenuItem = (item: MenuItem, nested = false) => {
    const Icon = item.icon;
    const link = (
      <Link
        to={item.path}
        onClick={handleMobileClose}
        className={cn(
          "flex items-center gap-3 px-2 py-2 rounded-lg transition-all duration-200 group border border-transparent",
          nested ? "h-9" : "h-10",
          item.isActive
            ? "bg-gray-100 text-gray-900 border border-gray-200"
            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
        )}
      >
        <Icon className={cn(
          "flex-shrink-0 border border-transparent",
          nested ? "h-4 w-4" : "h-5 w-5",
          item.isActive ? "text-gray-700" : "text-gray-500 group-hover:text-gray-700"
        )} />
        <div className={cn(
          "flex items-center gap-2 flex-1 min-w-0 transition-all duration-300 ease-in-out overflow-hidden",
          isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
        )}>
          <span className={cn("font-medium truncate border border-transparent", nested && "text-sm")}>{item.name}</span>
          {item.badge && (
            <Badge variant="default" className="text-xs px-1.5 py-0.5 bg-gray-200 hover:bg-gray-200 text-gray-700">
              {item.badge}
            </Badge>
          )}
        </div>
      </Link>
    );

    if (!isCollapsed) return link;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {link}
        </TooltipTrigger>
        <TooltipContent side="right" className="flex items-center gap-2">
          <span>{item.name}</span>
          {item.badge && (
            <Badge variant="default" className="text-sm px-1.5 py-0.5 bg-gray-200 hover:bg-gray-200 text-gray-700">
              {item.badge}
            </Badge>
          )}
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className={cn(
        "flex flex-col bg-white transition-all duration-300 ease-in-out h-full",
        // Desktop: larghezza animata, Mobile: nessun bordo (modal)
        "md:border-r md:border-gray-200",
        isCollapsed ? "md:w-[70px]" : "md:w-[222px]"
      )}>
        {/* Agency Logo Section - shown when agency logo is set */}
        {agencyLogoUrl && (
          <div className="flex h-[80px] items-center justify-center p-3 border-b border-gray-100">
            <img
              src={isCollapsed && agencyLogoSmallUrl ? agencyLogoSmallUrl : agencyLogoUrl}
              alt="Logo agenzia"
              className={cn(
                "object-contain transition-all duration-300 ease-in-out",
                isCollapsed ? "h-8 w-8" : "h-10 max-w-[160px] w-auto"
              )}
            />
          </div>
        )}

        {/* Logo Section - always rendered to reserve space; hidden while loading or when an agency logo is shown */}
        {!agencyLogoUrl && <div
          className="flex h-[80px] items-center justify-center items-center p-4 border-b border-gray-200"
          style={{ visibility: logoLoading ? 'hidden' : 'visible' }}
        >
          <Link to="/dashboard" className="flex items-center justify-start group border border-transparent">
            <div className="transition-all duration-300 ease-in-out">
              {isCollapsed ? (
                <img
                  src="/assets/images/dark_small.png"
                  alt="Place2Be Logo"
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <img
                  src="/assets/images/dark_extended.png"
                  alt="Place2Be Logo"
                  className="h-6 w-auto"
                />
              )}
            </div>
          </Link>
        </div>}

        {/* Navigation Menu */}
        <nav className="flex-1 py-4 px-3 overflow-y-auto border border-transparent">
          <ul className="space-y-2 border border-transparent">
            {menuGroups.map((group, groupIndex) => {
              // Sidebar compressa: niente accordion (non c'e' spazio per le etichette),
              // tutte le voci come icone con una linea fra un gruppo e l'altro.
              // Stesso trattamento per i gruppi con una sola voce (es. il viaggiatore
              // vede solo Pagamenti): un accordion con un figlio unico non serve.
              if (isCollapsed || !group.title || !group.icon || group.items.length === 1) {
                return (
                  <li key={group.key} className="border border-transparent">
                    {isCollapsed && groupIndex > 0 && (
                      <div className="mx-2 mb-2 border-t border-gray-200" />
                    )}
                    <ul className="space-y-2">
                      {group.items.map((item) => (
                        <li key={item.path}>{renderMenuItem(item)}</li>
                      ))}
                    </ul>
                  </li>
                );
              }

              const GroupIcon = group.icon;
              const isOpen = openGroups[group.key] ?? false;
              const hasActiveItem = group.items.some((item) => item.isActive);
              const panelId = `sidebar-group-${group.key}`;

              return (
                <li key={group.key} className="border border-transparent">
                  <div
                    className={cn(
                      "flex items-center rounded-lg transition-all duration-200 border border-transparent h-10",
                      hasActiveItem && !isOpen
                        ? "bg-gray-100 text-gray-900 border-gray-200"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    )}
                  >
                    {/* Il titolo porta alla prima pagina della sezione e apre l'accordion.
                        Non chiude il drawer mobile, cosi' le altre pagine restano visibili. */}
                    <Link
                      to={group.items[0].path}
                      onClick={() => setGroupOpen(group.key, true)}
                      className="group flex items-center gap-3 px-2 py-2 flex-1 min-w-0 h-full"
                    >
                      <GroupIcon className={cn(
                        "h-5 w-5 flex-shrink-0",
                        hasActiveItem ? "text-gray-700" : "text-gray-500 group-hover:text-gray-700"
                      )} />
                      <span className="font-medium truncate">{group.title}</span>
                    </Link>
                    <button
                      type="button"
                      onClick={() => setGroupOpen(group.key, !isOpen)}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      aria-label={group.title}
                      className="h-8 w-8 mr-1 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex-shrink-0"
                    >
                      <ChevronDown className={cn(
                        "h-4 w-4 transition-transform duration-200",
                        isOpen && "rotate-180"
                      )} />
                    </button>
                  </div>
                  {/* Il pannello resta sempre montato: l'altezza si anima con grid-rows
                      0fr→1fr (funziona con contenuto ad altezza variabile, a differenza di
                      max-height). Da chiuso è inert, così le voci non ricevono il focus. */}
                  <div
                    id={panelId}
                    {...({ inert: isOpen ? undefined : "" } as Record<string, unknown>)}
                    className={cn(
                      "grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none",
                      isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    )}
                  >
                    <div className="min-h-0 overflow-hidden">
                      <ul
                        className={cn(
                          "mt-1 ml-4 pl-2 space-y-1 border-l border-gray-200 transition-transform duration-200 ease-out motion-reduce:transition-none",
                          isOpen ? "translate-y-0" : "-translate-y-1"
                        )}
                      >
                        {group.items.map((item) => (
                          <li key={item.path}>{renderMenuItem(item, true)}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="flex flex-col items-start gap-1 pb-2 mx-2 font-medium">
          {userRole === 'traveler' && (() => {
            const place2beLink = (
              <a
                href="https://www.place2be.it"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "flex items-center gap-3 px-2 py-2 rounded-lg transition-all duration-200 group border border-transparent h-10 w-full",
                  "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                )}
                aria-label="Place2Be"
              >
                <img src="/assets/images/dark_small.png" alt="Place2Be" className="h-5 w-5 flex-shrink-0" />
                <div className={cn(
                  "flex items-center gap-2 flex-1 min-w-0 transition-all duration-300 ease-in-out overflow-hidden",
                  isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
                )}>
                  <span className="font-medium truncate">Place2Be.it</span>
                </div>
                {!isCollapsed && (
                  <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
                )}
              </a>
            );

            return (
              <div className="w-full border border-transparent">
                {isCollapsed ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {place2beLink}
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <span>Place2Be.it</span>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  place2beLink
                )}
              </div>
            );
          })()}
          {userRole !== 'traveler' && (() => {
            const notionLink = (
              <a
                href="https://www.notion.so/32ad0dbdf1f5804fabd4c9bf0a503846?v=32ad0dbdf1f5805bbbdd000cd9e66572&source=copy_link"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "flex items-center gap-3 px-2 py-2 rounded-lg transition-all duration-200 group border border-transparent h-10 w-full",
                  "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                )}
                aria-label="TA Hub"
              >
                <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.981-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.887l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952L12.21 19s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.14c-.093-.514.28-.887.747-.933zM1.936 1.035l13.31-.98c1.634-.14 2.055-.047 3.082.7l4.249 2.986c.7.513.934.653.934 1.213v16.378c0 1.026-.373 1.634-1.68 1.726l-15.458.934c-.98.047-1.448-.093-1.962-.747l-3.129-4.06c-.56-.747-.793-1.306-.793-1.96V2.667c0-.839.374-1.54 1.447-1.632z"/>
                </svg>
                <div className={cn(
                  "flex items-center gap-2 flex-1 min-w-0 transition-all duration-300 ease-in-out overflow-hidden",
                  isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
                )}>
                  <span className="font-medium truncate">TA Hub</span>
                </div>
                {!isCollapsed && (
                  <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
                )}
              </a>
            );

            const expediaLink = (
              <a
                href="https://www.ratehawk.com/lp/it/?referral_code=GEOWAL"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "flex items-center gap-3 px-2 py-2 rounded-lg transition-all duration-200 group border border-transparent h-10 w-full",
                  "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                )}
                aria-label="Booking portal"
              >
                <img src="/assets/images/dark_small.png" alt="Booking portal" className="h-5 w-5 flex-shrink-0" />
                <div className={cn(
                  "flex items-center gap-2 flex-1 min-w-0 transition-all duration-300 ease-in-out overflow-hidden",
                  isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
                )}>
                  <span className="font-medium truncate">{t('sidebar.bookingPortal')}</span>
                </div>
                {!isCollapsed && (
                  <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
                )}
              </a>
            );

            const arubaLink = (
              <a
                href="https://webmail.aruba.it/"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "flex items-center gap-3 px-2 py-2 rounded-lg transition-all duration-200 group border border-transparent h-10 w-full",
                  "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                )}
                aria-label="Aruba Webmail"
              >
                <svg className="h-5 w-5 flex-shrink-0 text-gray-700" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 8l9 6 9-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
                </svg>
                <div className={cn(
                  "flex items-center gap-2 flex-1 min-w-0 transition-all duration-300 ease-in-out overflow-hidden",
                  isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
                )}>
                  <span className="font-medium truncate">Aruba Webmail</span>
                </div>
                {!isCollapsed && (
                  <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
                )}
              </a>
            );

            return (
              <>
                {/* <div className="w-full border border-transparent">
                  {isCollapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {notionLink}
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <span>TA Hub</span>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    notionLink
                  )}
                </div> */}

                <div className="w-full border border-transparent">
                  {isCollapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {expediaLink}
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <span>{t('sidebar.bookingPortal')}</span>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    expediaLink
                  )}
                </div>

                {/* <div className="w-full border border-transparent">
                  {isCollapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {arubaLink}
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <span>Aruba Webmail</span>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    arubaLink
                  )}
                </div> */}
              </>
            );
          })()}
          {!isStandalone && (() => {
            const installBtn = (
              <button
                onClick={handleInstall}
                className={cn(
                  "flex items-center gap-3 px-2 py-2 rounded-lg transition-all duration-200 group border border-transparent h-10 w-full",
                  "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                )}
              >
                <Download className="h-5 w-5 flex-shrink-0 text-gray-500 group-hover:text-gray-700" />
                <div className={cn(
                  "flex items-center gap-2 flex-1 min-w-0 transition-all duration-300 ease-in-out overflow-hidden",
                  isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"
                )}>
                  <span className="font-medium truncate">{t('sidebar.downloadApp')}</span>
                </div>
              </button>
            );
            return (
              <div className="w-full border border-transparent">
                {isCollapsed ? (
                  <Tooltip>
                    <TooltipTrigger asChild>{installBtn}</TooltipTrigger>
                    <TooltipContent side="right"><span>{t('sidebar.downloadApp')}</span></TooltipContent>
                  </Tooltip>
                ) : installBtn}
              </div>
            );
          })()}
        </div>
        {/* Bottom actions - Only on Desktop */}
        <div className="p-4 border-t border-gray-200 md:flex hidden items-center justify-between">
          <div className="ml-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              className="h-8 w-8 rounded-full hover:bg-gray-100 transition-all duration-300 ease-in-out"
            >
              <div className="transition-transform duration-300 ease-in-out">
                {isCollapsed ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <ChevronLeft className="h-4 w-4" />
                )}
              </div>
            </Button>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default Sidebar;
